import React, { memo, useCallback, useMemo, type PropsWithChildren, type ReactNode } from 'react';
import type { PreloadedQuery } from 'react-relay';
import { ProjectStyle } from '@jira/platform__project-types/src/project-types.tsx';
import { isLoaderErrorAttributes } from '@jira/platform__react-loosely-lazy-error-handling/src/is-loader-error-attributes.tsx';
import {
	useJqlBuilderProps,
	useServerTimeUFO,
	type NestedEntryPoints,
} from '@atlassian/jira-spa-apps-issue-navigator-common/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { setIsGlobalScopeEnrolmentEnabledInLocalStorage } from '@atlassian/jira-issue-navigator-global-scope-enrolment/src/services/global-scope-enrolment/local-storage/index.tsx';
import type { View } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import { NINPageLoad } from '@atlassian/jira-issue-navigator/src/common/utils/performance-analytics.tsx';
import { SearchInputTypes } from '@atlassian/jira-issue-navigator/src/common/constants.tsx';
import {
	getJqlFromParamValues,
	getSearchInput as getInput,
	useUpdateUrlQueryParamCallback,
} from '@atlassian/jira-issue-navigator/src/common/utils/spa-utils.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import ProjectIssueNavigatorApp, {
	type Props as ProjectIssueNavigatorProps,
} from '@atlassian/jira-project-issue-navigator/src/index.tsx';
import {
	appBundleReadyMark,
	prefetchApiReadyMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { isNinGlobalScopeEnrolmentEnabledResource } from '@atlassian/jira-router-resources-is-nin-global-scope-enrolment-enabled/src/index.tsx';
import {
	issueNavigatorFilterRelayResource,
	issueNavigatorRelayResource,
	issueNavigatorHydrationRelayResource as issueNavigatorHydrationRelayResourceFromServer,
	issueNavigatorHydrationRelayResourceBrowserOnly,
	issueNavigatorFilterRefinementRelayResource,
} from '@atlassian/jira-router-resources-issue-navigator/src/index.tsx';
import { IssueNavigatorSubmitApdex } from '@atlassian/jira-spa-apps-issue-navigator-common/src/IssueNavigatorSubmitApdex.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { ProjectIssueNavigatorSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/project-app.tsx';
import {
	getDefaultJql,
	mapFilterToJql,
	isFilterKey,
} from '@atlassian/jira-spa-apps-project-issue-navigator-utils/src/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { IssueNavigatorFeatureProvider } from '@atlassian/jira-issue-navigator/src/controllers/features-context/index.tsx';
import type { useHydrationQueryLoaderQuery } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderWithAIFilterRefinementQuery } from '@atlassian/jira-jql-builder-with-ai/src/ui/filters/filter-refinement/__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql';
import { WelcomeModal } from './welcome-modal/index.tsx';

type CommonProps = Omit<
	ProjectIssueNavigatorProps,
	'defaultJql' | 'queryReference' | 'filterQueryReference' | 'searchInput' | 'issueTableProps'
>;

export type Props = CommonProps & {
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	jql: string | undefined;
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	filter: string | undefined;
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	initialised: boolean;
	/** @deprecated Behind jira_nin_last_used_jql_tech_changes. Remove on cleanup */
	view?: View;
	showNav3Header?: boolean;
	preloadedEntryPoints?: NestedEntryPoints;
};

const getSearchInput = (
	projectKey: string,
	jql: string | undefined,
	filter: string | undefined,
) => {
	const filterInput = isFilterId(filter) ? filter : mapFilterToJql(projectKey, filter);
	return getInput(jql, filterInput);
};

const getFilterForAnalytics = (filter: string | undefined) => {
	return filter && (isFilterId(filter) || isFilterKey(filter)) ? filter : 'default';
};

const useProjectAttributes = () => {
	const { data: projectContext } = useResource(projectContextResource);

	let resolvedProjectConfig;
	switch (projectContext?.simplified) {
		case true:
			resolvedProjectConfig = ProjectStyle.NEXT_GEN;
			break;
		case false:
			resolvedProjectConfig = ProjectStyle.CLASSIC;
			break;
		default:
			resolvedProjectConfig = '';
	}
	const resolvedProjectType = projectContext?.projectType || '';

	return useMemo(
		() => ({
			projectConfig: resolvedProjectConfig,
			projectType: resolvedProjectType,
		}),
		[resolvedProjectConfig, resolvedProjectType],
	);
};

const useIssueTableProps = (projectKey: string, preloadedEntryPoints?: NestedEntryPoints) => {
	return useMemo(
		() => ({
			projectContext: projectKey,
			isGroupingSupported:
				expVal('jsc_m2_hierarchy', 'isHierarchyEnabled', false) &&
				expVal('jira_spreadsheet_component_grouping', 'isGroupingEnabled', false),
			preloadedEntryPoints: {
				nativeIssueTableEntryPoint: preloadedEntryPoints?.nativeIssueTableEntryPoint,
			},
		}),
		[projectKey, preloadedEntryPoints?.nativeIssueTableEntryPoint],
	);
};

const useGlobalScopeEnrolment = () => {
	const { data: globalEnrolmentData } = useResource(isNinGlobalScopeEnrolmentEnabledResource);
	if (fg('nin_remove_global_scope_enrolment')) {
		return;
	}
	if (globalEnrolmentData !== null && globalEnrolmentData !== undefined) {
		setIsGlobalScopeEnrolmentEnabledInLocalStorage(globalEnrolmentData);
	}
};

/**
 * Default search input value for type safety when `jira_nin_last_used_jql_tech_changes` is enabled.
 * Remove on FG cleanup.
 */
const deprecatedSearchInput = { jql: '', type: SearchInputTypes.JQL };

const MaybeReportErrors = componentWithFG(
	'improve_nin_slo_events',
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
	ReportErrors,
);

const AsyncProjectIssueNavigatorWrapper = ({
	filter,
	jql: jqlParam,
	initialised,
	view,
	projectKey,
	showNav3Header = false,
	...props
}: Props) => {
	const getServerTimeUFO = useServerTimeUFO();

	const defaultJql = getDefaultJql(projectKey);

	let onFilterKeyToJql;
	let searchInput;
	if (fg('jira_nin_last_used_jql_tech_changes')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		onFilterKeyToJql = useCallback(
			(filterToMap: string) => mapFilterToJql(projectKey, filterToMap),
			[projectKey],
		);
		searchInput = deprecatedSearchInput;
	} else {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const onMutateJqlParam = useUpdateUrlQueryParamCallback('jql');
		const jql = getJqlFromParamValues(
			defaultJql,
			jqlParam,
			filter,
			mapFilterToJql.bind(undefined, projectKey),
			onMutateJqlParam,
		);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		searchInput = useMemo(() => getSearchInput(projectKey, jql, filter), [projectKey, jql, filter]);
	}

	const issueTableProps = useIssueTableProps(projectKey, props.preloadedEntryPoints);

	const projectAttributes = useProjectAttributes();
	useGlobalScopeEnrolment();

	const response = useRelayResource(issueNavigatorRelayResource);
	const { queryReference } = response;

	const filterResponse = useRelayResource(issueNavigatorFilterRelayResource);
	const filterQueryReference = filterResponse.queryReference;

	let hydrationQueryReference: PreloadedQuery<useHydrationQueryLoaderQuery> | null = null;
	let filterRefinementQueryReference: PreloadedQuery<JQLBuilderWithAIFilterRefinementQuery> | null =
		null;
	if (expVal('list_lovability_improving_filters_exp', 'isEnabled', false)) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const filterRefinementResponse = useRelayResource(issueNavigatorFilterRefinementRelayResource);
		filterRefinementQueryReference = filterRefinementResponse.queryReference;
	} else {
		const issueNavigatorHydrationRelayResource = fg('render_jql_builder_in_ssr')
			? issueNavigatorHydrationRelayResourceFromServer
			: issueNavigatorHydrationRelayResourceBrowserOnly;

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const hydrationResponse = useRelayResource(issueNavigatorHydrationRelayResource);
		hydrationQueryReference = hydrationResponse.queryReference;
	}

	const jqlBuilderProps = useJqlBuilderProps(
		hydrationQueryReference,
		props.preloadedEntryPoints,
		expVal('list_lovability_improving_filters_exp', 'isEnabled', false) ? projectKey : undefined,
	);

	const attributesNew = useMemo(
		() =>
			fg('jira_nin_last_used_jql_tech_changes')
				? {
						navigatorScope: 'project',
						...(fg('analytics_for_issue_key_included_in_nin_url') && {
							isIssueKeyPresent: !!props.issueKey,
						}),
						...projectAttributes,
					}
				: undefined,
		[projectAttributes, props.issueKey],
	);

	// During SSR our issueNavigatorRelayResource must wait until the query finishes before returning the
	// queryReference as SSR does not support Suspense. We return the page skeleton as an intermediary state until
	// the query has resolved (or SSR times out and the query is loaded on the client).
	if (queryReference == null) {
		return <ProjectIssueNavigatorSkeleton />;
	}

	// During SSR our issueNavigatorFilterRelayResource must wait until the query finishes before returning the
	// queryReference as SSR does not support Suspense. We return the page skeleton as an intermediary state until
	// the query has resolved (or SSR times out and the query is loaded on the client).
	if (filterQueryReference == null) {
		return <ProjectIssueNavigatorSkeleton />;
	}

	// During SSR our issueNavigatorHydrationRelayResource must wait until the query finishes before returning the
	// queryReference as SSR does not support Suspense. We return the page skeleton as an intermediary state until
	// the query has resolved (or SSR times out and the query is loaded on the client).
	if (
		hydrationQueryReference == null &&
		!expVal('list_lovability_improving_filters_exp', 'isEnabled', false) &&
		fg('render_jql_builder_in_ssr')
	) {
		return <ProjectIssueNavigatorSkeleton />;
	}

	const attributes = fg('jira_nin_last_used_jql_tech_changes')
		? attributesNew
		: {
				navigatorScope: 'project',
				viewLayout: view || '',
				jql: !!jqlParam,
				filter: getFilterForAnalytics(filter),
				...projectAttributes,
			};

	const extraInfo = {
		scope: 'project',
		filterId: getFilterForAnalytics(filter),
		isJqlParamPresent: !!jqlParam,
		...(fg('analytics_for_issue_key_included_in_nin_url') && {
			isIssueKeyPresent: !!props.issueKey,
		}),
		...(initialised && getServerTimeUFO?.()),
		...attributes,
	};

	return (
		<ContextualAnalyticsData
			attributes={attributes}
			{...(filter !== null &&
				filter !== undefined &&
				(isFilterId(filter) || isFilterKey(filter)) &&
				!fg('jira_nin_last_used_jql_tech_changes') && {
					containers: { issueFilter: { id: filter } },
				})}
		>
			<MaybeReportErrors
				id="unhandled"
				packageName="jiraSpaAppsProjectIssueNavigator"
				teamName="empanada"
				sendToPrivacyUnsafeSplunk
				attributes={isLoaderErrorAttributes}
			>
				{/* Render a Suspense boundary to handle the scenario where the resource query times out in SSR and
                content (i.e. nav) is still returned. Ideally we'd use the SPA level suspense boundary but there is some
                internal state logic in RLL that is preventing the fallback being rendered after the async component has
                successfully imported. */}
				<Placeholder
					name="project-issue-navigator-app"
					fallback={<ProjectIssueNavigatorSkeleton />}
				>
					<>
						<FeatureProvider
							isConditionalFormattingEnabled={fg('jira_list_conditional_formatting')}
							isConditionalFormattingEditingEnabled={fg('saved_list_view_technical_changes')}
							isSaveFilterButtonEnabled={
								!expVal('jira-issue-navigator-saved-filters-menu', 'isEnabled', false)
							}
							isProjectQuickstartOnboardingEnabled={fg('jira_list_nin_merge')}
							isMakeSpaceForGlobalRovoButtonEnabled={fg('global_rovo_button_in_jira')}
						>
							<ProjectIssueNavigatorApp
								{...props}
								showNav3Header={showNav3Header}
								projectKey={projectKey}
								defaultJql={defaultJql}
								queryReference={queryReference || undefined}
								filterQueryReference={filterQueryReference}
								searchInput={searchInput}
								onFilterKeyToJql={onFilterKeyToJql}
								issueTableProps={issueTableProps}
								isIssueHierarchySupportEnabled={expVal(
									'jsc_m2_hierarchy',
									'isHierarchyEnabled',
									false,
								)}
								isHideDoneSupportEnabled={expVal('jira_list_hide_done_items', 'isEnabled', false)}
								isIssueRankingSupportEnabled={fg('jsc_list_reranking_gate')}
								jqlBuilderProps={
									fg('jql_builder_basic_ssr_stability_fixes')
										? jqlBuilderProps
										: { maybeHydrationQueryReference: hydrationQueryReference || undefined }
								}
								isMergedColumnSupportEnabled={fg('jira_list_merged_column_gate')}
								isFrozenColumnSupportEnabled={fg('issue_table_frozen_columns')}
								isGridNavigationEnabled={expVal(
									'nin_grid_navigation_experiment',
									'isGridNavigationEnabled',
									false,
								)}
								isInlineFieldConfigEnabled={expVal(
									'jira_inline_field_config',
									'isInlineFieldConfigEnabled',
									false,
								)}
								isInBetweenColumnPickerEnabled={fg('jira_inline_field_config_gate')}
								isPostOfficePlacementEnabled={!showNav3Header && fg('jira_list_nin_merge')}
								isFilterRefinementEnabled={expVal(
									'list_lovability_improving_filters_exp',
									'isEnabled',
									false,
								)}
								filterRefinementQueryReference={filterRefinementQueryReference}
							/>
						</FeatureProvider>
						{fg('jira_nin_last_used_jql_tech_changes') ? (
							<>
								<IssueNavigatorSubmitApdex
									appName="project-issue-navigator"
									attributes={attributes}
									scope="project"
								/>
								{!__SERVER__ && <WelcomeModal />}
							</>
						) : null}
					</>
				</Placeholder>
			</MaybeReportErrors>
			{initialised && !fg('jira_nin_last_used_jql_tech_changes') ? (
				<>
					{/* @ts-expect-error - Types for JFE BM3Metric and @atlassian/browser-metrics' PageLoadMetric are not compatible */}
					<SubmitApdex
						appName="project-issue-navigator"
						isExpectedToHaveSsr
						withMarks={[appBundleReadyMark, prefetchApiReadyMark]}
						ttrSlo={3000}
						ttiSlo={4000}
						extra={extraInfo}
						metric={NINPageLoad}
					/>
					<SpaStatePageReady />
					<WelcomeModal />
				</>
			) : null}
		</ContextualAnalyticsData>
	);
};

const NoOp = ({ children }: { children: ReactNode }) => children;
export const FeatureProvider = componentWithFG(
	'jira_list_conditional_formatting',
	IssueNavigatorFeatureProvider,
	NoOp,
);

export default memo<Props>(AsyncProjectIssueNavigatorWrapper);
